import { useCallback, useEffect, useRef, useState } from "react";

import { ErrorApi } from "../../lib/api/errores";
import { useTransporte } from "../../lib/api/transporte";
import type { CVDocument } from "../../lib/document/model";
import { toDirectus } from "../../lib/document/toDirectus";

/**
 * Autoguardado con espera.
 *
 * Sustituye al "Recuerda guardar cambios de cada sección antes de continuar"
 * del builder viejo: si el producto necesita advertir eso, el guardado esta mal
 * puesto. Aqui no hay boton de guardar.
 */

export type EstadoGuardado =
  | {
      fase: "limpio";
      ultimo: Date | null;
      /** Campos que el servidor no acepto: se guardo todo menos esto. */
      omitidos?: string[];
    }
  | { fase: "pendiente" }
  | { fase: "guardando" }
  | { fase: "error"; mensaje: string; reintentable: boolean };

const ESPERA_MS = 1200;

export const useAutoguardado = (
  cvId: number,
  doc: CVDocument | null,
  revision: number,
  /** Se llama con lo que devolvio el servidor, para adoptar los ids reales. */
  onGuardado: (guardado: CVDocument) => void,
) => {
  const transporte = useTransporte();
  const [estado, setEstado] = useState<EstadoGuardado>({ fase: "limpio", ultimo: null });

  /**
   * El documento tal como esta en el servidor. Es la referencia contra la que
   * `toDirectus` calcula que filas hay que borrar, y solo se actualiza cuando
   * un guardado termina bien: si se actualizara antes, un fallo dejaria el
   * calculo de borrados mirando a un estado que nunca llego a existir.
   */
  const servidor = useRef<CVDocument | null>(null);
  const enVuelo = useRef(false);
  const pendiente = useRef(false);
  const ultimaRevision = useRef(0);

  const adoptarServidor = useCallback((d: CVDocument) => {
    servidor.current = d;
  }, []);

  const guardarAhora = useCallback(async () => {
    if (!doc || enVuelo.current) {
      pendiente.current = true;
      return;
    }

    enVuelo.current = true;
    setEstado({ fase: "guardando" });

    try {
      const { doc: guardado, omitidos } = await transporte.guardar(
        cvId,
        toDirectus(doc, servidor.current),
      );
      servidor.current = guardado;
      onGuardado(guardado);
      setEstado({ fase: "limpio", ultimo: new Date(), omitidos });
    } catch (e) {
      const red = e instanceof ErrorApi && e.status === 0;
      setEstado({
        fase: "error",
        mensaje: red
          ? "Sin conexión. Seguimos intentando."
          : e instanceof ErrorApi
            ? e.message
            : "No pudimos guardar los cambios.",
        // Un 4xx no se arregla repitiendolo: el servidor ya dijo que no. Solo
        // se reintenta lo que pudo fallar por el camino.
        reintentable: red,
      });
    } finally {
      enVuelo.current = false;
      if (pendiente.current) {
        pendiente.current = false;
        void guardarAhora();
      }
    }
  }, [cvId, doc, onGuardado, transporte]);

  // Espera tras el ultimo cambio. Cada tecla reinicia el reloj, asi que
  // escribir un parrafo entero es UNA peticion, no una por letra.
  useEffect(() => {
    if (!doc || revision === 0 || revision === ultimaRevision.current) return;
    ultimaRevision.current = revision;
    setEstado({ fase: "pendiente" });

    const t = setTimeout(() => void guardarAhora(), ESPERA_MS);
    return () => clearTimeout(t);
  }, [doc, revision, guardarAhora]);

  // Un ultimo intento al cerrar la pestaña. `sendBeacon` no sirve: Directus
  // necesita PATCH y el beacon solo hace POST. Lo que si se puede es avisar.
  useEffect(() => {
    const alSalir = (e: BeforeUnloadEvent) => {
      if (estado.fase === "pendiente" || estado.fase === "guardando") {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", alSalir);
    return () => window.removeEventListener("beforeunload", alSalir);
  }, [estado.fase]);

  return { estado, guardarAhora, adoptarServidor };
};
